import { Component, input, linkedSignal, ChangeDetectionStrategy } from '@angular/core';
import { ColumnRendererComponentV3 } from '@gea/digital-ui-lib';
import { TreeNode } from 'primeng/api';
import { MachineFaultDto } from '@shared/models';

export type AnnunciationTree = TreeNode<MachineFaultDto>;

interface ProgressBarConfig {
  barHeight: number;
  countColor: string;
  totalCountColor: string;
}

/* Helper structures to avoid `any` while describing loose shapes */
interface NodeCarrier {
  node?: unknown;
  rowData?: unknown;
  data?: unknown;
}

interface KeyCarrier {
  key?: unknown;
  field?: unknown;
}

/* Generic object check */
function isRecord(val: unknown): val is Record<string, unknown> {
  return typeof val === 'object' && val !== null;
}

/* Type guard for AnnunciationTree: keep it permissive but non-null object */
function isAnnunciationTree(val: unknown): val is AnnunciationTree {
  return isRecord(val);
}

/* Type guard: object has `data` */
function hasData(obj: unknown): obj is { data: unknown } {
  return isRecord(obj) && 'data' in obj;
}

/* Type guard: object has `children` as an array */
function hasChildrenArray(o: unknown): o is { children: unknown[] } {
  return isRecord(o) && 'children' in o && Array.isArray((o as { children?: unknown }).children);
}

/* Extract row context safely */
function getRowFromContext(v: unknown): AnnunciationTree | undefined {
  if (isRecord(v)) {
    const obj = v as NodeCarrier;

    if (obj.node && isAnnunciationTree(obj.node)) return obj.node;
    if (obj.rowData && isAnnunciationTree(obj.rowData)) return obj.rowData;
    if (obj.data && isAnnunciationTree(obj.data)) return obj.data;
  }

  return isAnnunciationTree(v) ? v : undefined;
}

type ColumnKey = string;

/* Validate column key */
function isColumnKey(v: unknown): v is ColumnKey {
  return typeof v === 'string' && v.length > 0;
}

/* Extract key safely */
function getKeyFromColumn(v: unknown): ColumnKey | undefined {
  if (isRecord(v)) {
    const obj = v as KeyCarrier;

    if (obj.key && isColumnKey(obj.key)) return obj.key;
    if (obj.field && isColumnKey(obj.field)) return obj.field;
  }
  return isColumnKey(v) ? v : undefined;
}

/* Safe property getter */
function getProp(obj: unknown, prop: ColumnKey): unknown {
  return isRecord(obj) ? obj[prop] : undefined;
}

/* Safe data getter */
function getData(obj: unknown): unknown {
  return hasData(obj) ? obj.data : undefined;
}

/* Safe children getter � no spread over unknown */
function getChildren(obj: unknown): unknown[] {
  return hasChildrenArray(obj) ? obj.children : [];
}

@Component({
  selector: 'gea-hrt-progress-bar-renderer',
  template: `
    <div class="progressbar-wrapper" role="img" aria-label="Progress bar">
      <span
        class="progressbar-count"
        [style.backgroundColor]="effectiveConfig().countColor"
        [style.height.px]="effectiveConfig().barHeight"
        [style.width.%]="getCountWidth()"></span>
      <span
        class="progressbar-total-count"
        [style.backgroundColor]="effectiveConfig().totalCountColor"
        [style.height.px]="effectiveConfig().barHeight"
        [style.width.%]="getTotalCountWidth()"></span>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }

      .progressbar-wrapper {
        display: flex;
        flex-direction: row;
        position: relative;
        z-index: 1;
        width: 100%;
        min-width: 0;
        box-sizing: border-box;
      }

      .progressbar-count,
      .progressbar-total-count {
        display: inline-block;
        position: static;
        box-sizing: border-box;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressBarRendererComponent implements ColumnRendererComponentV3<AnnunciationTree> {
  readonly rowData = input<AnnunciationTree>();
  readonly row = input<AnnunciationTree>();
  readonly data = input<AnnunciationTree>();
  readonly node = input<AnnunciationTree>();
  readonly context = input<unknown>();
  readonly columnKey = input<string>();
  readonly field = input<string>();
  readonly key = input<string>();
  readonly column = input<unknown>();
  readonly config = input<ProgressBarConfig>();

  private readonly selectedRow = linkedSignal({
    source: () => ({ rowData: this.rowData(), row: this.row(), data: this.data(), node: this.node(), context: this.context() }),
    computation: (source, previous): AnnunciationTree | undefined => {
      let selected = previous?.value;
      for (const key of Object.keys(source) as (keyof typeof source)[]) {
        if (previous ? source[key] !== previous.source[key] : source[key] !== undefined) {
          selected = key === 'context' ? getRowFromContext(source[key]) : (source[key] as AnnunciationTree | undefined);
        }
      }
      return selected;
    },
  });
  private readonly selectedKey = linkedSignal({
    source: () => ({ columnKey: this.columnKey(), field: this.field(), key: this.key(), column: this.column() }),
    computation: (source, previous): string => {
      let selected = previous?.value ?? 'countPendingRelative';
      for (const key of Object.keys(source) as (keyof typeof source)[]) {
        if (!previous || source[key] !== previous.source[key]) {
          const value = key === 'column' ? getKeyFromColumn(source[key]) : source[key];
          if (isColumnKey(value)) selected = value;
        }
      }
      return selected;
    },
  });
  readonly effectiveConfig = linkedSignal({
    source: () => this.config(),
    computation: (config, previous): ProgressBarConfig => ({
      barHeight: 20,
      countColor: 'var(--geaui-yellow)',
      totalCountColor: 'var(--geaui-light-gray)',
      ...previous?.value,
      ...config,
    }),
  });

  private readonly availablePercent = 100 as const;

  getCountWidth(): number {
    return Math.round(this.valueFraction() * this.availablePercent);
  }

  getTotalCountWidth(): number {
    return Math.max(0, Math.round((1 - this.valueFraction()) * this.availablePercent));
  }

  /* Core logic with strict typing */
  private valueFraction(): number {
    const key = this.selectedKey();
    const node = this.selectedRow();

    if (!node || !key) return 0;

    let raw: unknown = getProp(getData(node), key);

    // compute from children if empty
    const children = getChildren(node);
    if ((raw == null || raw === '') && children.length) {
      const nums = children
        .map((c) => {
          const v = getProp(getData(c), key);
          const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
          return Number.isFinite(n) ? n : undefined;
        })
        .filter((n): n is number => n !== undefined);

      if (nums.length) {
        const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
        return this.clamp01(this.normalize(avg));
      }
    }

    // fallback to node property
    if (raw == null) raw = getProp(node, key);

    return this.clamp01(this.normalize(raw));
  }

  private normalize(v: unknown): number {
    if (typeof v === 'string' && v.trim().endsWith('%')) {
      const n = Number(v.replace('%', '').trim());
      return Number.isFinite(n) ? n / 100 : 0;
    }
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return n > 1 ? n / 100 : n;
  }

  private clamp01(n: number): number {
    return Math.max(0, Math.min(1, n));
  }
}
